import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {SoundOutline } from 'antd-mobile-icons'
import AiChatLoader from "@/components/Loaders/AiChatLoader"
import SuggestionCard from './SuggestionCard';
import { useI18n } from '@/hooks/useI18n';
import GettingVoiceLoader from '@/components/Loaders/GettingVoiceLoader';

interface Message {
  id: string;
  type: 'user' | 'ai';
  content: string;
  speech_type: 'text' | 'video';
  duration?: number;
  isComplete?: boolean;

  audioPlayState?: 'idle' | 'loading' | 'playing';
  showText?: boolean;
  title?: string;
  // AI助答相关字段
  aiAssistance?: {
    hint_content: string;
    suggestions: string[];
    context: string;
  };
}

interface MessageItemProps {
  message: Message;
  index: number;
  isLast?: boolean;
  onPlayAudio?: (message: Message, index: number) => void;
  onViewTips?: (message: Message, index: number) => void;
  showPlayButton?: boolean;
  showAiAssistButton?: boolean;
}

const MessageItem: React.FC<MessageItemProps> = ({
  message,
  index,
  isLast = false,
  onPlayAudio,
  onViewTips,
  showPlayButton = true,
  showAiAssistButton = true
}) => {
  const { t } = useI18n();
  const [showTooltip, setShowTooltip] = useState(false);
  const [showAiAssistModal, setShowAiAssistModal] = useState(false);

  const handleAudioClick = () => {
    if (onPlayAudio) {
      onPlayAudio(message, index);
    }
  };

  const handleLongPress = () => {
    if (message.speech_type === 'video') {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 3000);
    }
  };

  const handleViewTips = () => {
      setShowAiAssistModal(true);
  };

  return (
    <motion.div
      className={`flex flex-col ${
        message.type === 'user' ? 'items-end' : 'items-start'
      }`}
      initial={{ opacity: 0, y: 20, scale: 0.8 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ 
        duration: 0.3,
        ease: 'easeOut'
      }}
    >
      {/* <div className="w-full p-2 border-b-gray-500 bg-yellow-50 px-3 py-2 rounded-2xl whitespace-pre-wrap">
        {Object.entries(message).map(([key, value]) => (
            <div key={key}>
              <strong>{key}:</strong> {JSON.stringify(value)}
            </div>
        ))}
      </div> */}

      <motion.div
        className={`rounded-2xl px-3 py-2 max-w-xs ${
          message.type === 'user' ? 'bg-purple-100' : 'bg-white'
        }`}
        style={{ width: message.duration ? `${65 + (message.duration || 0)}px` : 'auto' }}
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.2 }}
      >
        {message.speech_type === 'text' ? (
          <span className="inline-block text-left text-gray-600 text-sm font-normal leading-6">
            {message.content}
          </span>
        ) : (
          <motion.div
            className="h-7 leading-7 relative cursor-pointer"
            onClick={handleAudioClick}
            onContextMenu={(e) => {
              e.preventDefault();
              handleLongPress();
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="flex items-center h-7 space-x-2">
              <div className="flex items-center">
                <svg
                  className={`w-3 h-3 ${
                    message.type === 'user' ? 'text-purple-400' : 'text-yellow-500'
                  }`}
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
              </div>

              {message.audioPlayState === 'playing' ? (
                <motion.div 
                  className="flex space-x-1"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  {[1, 2, 3].map((i) => (
                    <motion.div
                      key={i}
                      className={`w-1 bg-current ${
                        message.type === 'user' ? 'text-purple-400' : 'text-gray-800'
                      }`}
                      style={{
                        height: `${8 + (i * 2)}px`
                      }}
                      animate={{
                        scaleY: [1, 1.5, 1],
                        opacity: [0.5, 1, 0.5]
                      }}
                      transition={{
                        duration: 0.6,
                        repeat: Infinity,
                        delay: i * 0.1
                      }}
                    />
                  ))}
                </motion.div>
              ) : (
                <svg
                  className={`w-4 h-4 ${
                    message.type === 'user' ? 'text-purple-400' : 'text-gray-800'
                  }`}
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}

              {message.duration && message.duration !== -1 && (
                <span
                  className={`text-xs ${
                    message.type === 'user' ? 'text-purple-400' : 'text-gray-800'
                  }`}
                >
                  {message.duration}s
                </span>
              )}
            </div>

            <AnimatePresence>
              {showTooltip && (
                <motion.div 
                  className="absolute top-8 left-0 bg-gray-800 text-white text-xs px-2 py-1 rounded z-10"
                  initial={{ opacity: 0, scale: 0.8, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.8, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {t('ai.training.convertToText')}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </motion.div>

      <AnimatePresence>
        {message.type === 'ai' && message.showText && message.isComplete && (
          <motion.div 
            className="p-2 text-sm leading-6 rounded-2xl block font-normal text-gray-600 mt-1 bg-gray-50"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            {message.title}
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI消息完成后显示的操作按钮 */}
      <AnimatePresence>
        {message.type === 'ai' && message.isComplete && (showPlayButton || showAiAssistButton) && (
          <motion.div 
            className="flex items-center justify-between mt-3 px-1"
            initial={{ opacity: 0, y: 10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            {/* 左侧播放按钮 */}
            {showPlayButton && (
              <motion.button
                onClick={handleAudioClick}
                className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors shadow-sm ${
                  message.audioPlayState === 'playing'
                    ? 'bg-purple-100 hover:bg-purple-200' 
                    : message.audioPlayState === 'loading'
                    ? 'bg-white shadow-none'
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                disabled={message.audioPlayState === 'loading'}
                title={
                  message.audioPlayState === 'playing'
                    ? t('ai.training.stopAudio')
                    : message.audioPlayState === 'loading'
                    ? t('ai.training.loadingAudio')
                    : t('ai.training.playAudio')
                }
              >
                {message.audioPlayState === 'playing' ? (
                  <motion.div 
                    className="flex items-center justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <svg
                      className="w-4 h-4 text-purple-600"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                    </svg>
                  </motion.div>
                ) : message.audioPlayState === 'loading' ? (
                
                   <GettingVoiceLoader/>
                ) : (
                  <SoundOutline />
                )}
              </motion.button>
            )}

            {/* 右侧AI助答按钮 */}
            {showAiAssistButton && (
              <motion.button
                onClick={handleViewTips}
                className="flex items-center space-x-2 ml-2 px-3 py-1.5 rounded-full bg-purple-50 hover:bg-purple-100 transition-colors border border-purple-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title={t('ai.training.aiAssist')}
              >
                <svg
                  className="w-3.5 h-3.5 text-purple-600"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z" />
                </svg>
                <span className="text-xs font-medium text-purple-600">{t('ai.training.aiAssist')}</span>
              </motion.button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI助答弹窗 */}
      <AnimatePresence>
        {showAiAssistModal && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowAiAssistModal(false)}
          >
            <motion.div
              className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto"
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* 弹窗标题 */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">{t('ai.training.aiAssist')}</h3>
                <button
                  onClick={() => setShowAiAssistModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* 内容区域 */}
              {message.aiAssistance ? (
                <>
                  {/* 提示内容 */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">{t('ai.training.suggestedReply')}</h4>
                    <p className="text-sm text-gray-600 bg-purple-50 p-3 rounded-lg">
                      {message.aiAssistance.hint_content}
                    </p>
                  </div>

         

                  {/* 上下文信息 */}
                  {message.aiAssistance.context && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">{t('ai.training.scenarioAnalysis')}</h4>
                      <p className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                        {message.aiAssistance.context}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <AiChatLoader />
                  <p className="text-sm text-gray-500 mt-4">{t('ai.training.analyzing')}</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default MessageItem;